import { type StateCreator, type StoreApi, type StoreMutatorIdentifier } from 'zustand';

type ActionObject<TArgs> = {
  [x: string | number | symbol]: unknown;
  type: string;
  args: TArgs;
};

type Action<TAction = object> = string | ActionObject<TAction>;

type StateChangeCallback<TAction, TState> = (
  action: ActionObject<TAction>,
  currentState: TState
) => TState | Partial<TState> | null;

type StateChanged = <
  TApi,
  TAction extends object,
  TMps extends [StoreMutatorIdentifier, unknown][] = [],
  TMcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  initializer: StateCreator<TApi, [...TMps, ['tipsen/stateChanged', TAction]], TMcs>,
  options: {
    actionName?: string;
    onUpdate: StateChangeCallback<TAction, TApi>;
  }
) => StateCreator<TApi, TMps, [['tipsen/stateChanged', TAction], ...TMcs]>;

type Cast<TArg, TObject> = TArg extends TObject ? TArg : TObject;
type Write<TApi, TArg> = Omit<TApi, keyof TArg> & TArg;
type TakeTwo<TArg> = TArg extends { length: 0 }
  ? [undefined, undefined]
  : TArg extends { length: 1 }
    ? [...a0: Cast<TArg, unknown[]>, a1: undefined]
    : TArg extends { length: 0 | 1 }
      ? [...a0: Cast<TArg, unknown[]>, a1: undefined]
      : TArg extends { length: 2 }
        ? TArg
        : TArg extends { length: 1 | 2 }
          ? TArg
          : TArg extends { length: 0 | 1 | 2 }
            ? TArg
            : TArg extends [infer A0, infer A1, ...unknown[]]
              ? [A0, A1]
              : TArg extends [infer A0, (infer A1)?, ...unknown[]]
                ? [A0, A1?]
                : TArg extends [(infer A0)?, (infer A1)?, ...unknown[]]
                  ? [A0?, A1?]
                  : never;

type StoreStateChangeArgs<TApi, TAction> = TApi extends {
  setState: infer SetState;
}
  ? SetState extends {
      (...a: infer A1): infer Sr1;
      (...a: infer A2): infer Sr2;
    }
    ? {
        setState(...a: [...a: TakeTwo<A1>, action?: Action<TAction>]): Sr1;
        setState(...a: [...a: TakeTwo<A2>, action?: Action<TAction>]): Sr2;
      }
    : never
  : never;

type WithStateChanged<TApi, TAction> = Write<TApi, StoreStateChangeArgs<TApi, TAction>>;

type NamedSet<TState> = WithStateChanged<StoreApi<TState>, object>['setState'];

declare module 'zustand' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface StoreMutators<S, A> {
    'tipsen/stateChanged': WithStateChanged<S, A>;
  }
}

type StateChangedImpl = <TState>(
  initializer: StateCreator<TState>,
  options: {
    actionName?: string;
    onUpdate: StateChangeCallback<object, TState>;
  }
) => StateCreator<TState>;

const stateChangedImpl: StateChangedImpl =
  (initializer, { actionName, onUpdate }) =>
  (set, get, api) => {
    type TState = ReturnType<typeof initializer> & Record<string, ReturnType<typeof initializer>>;

    // This function is a part of the Zustand API, hence the eslint rule can safely be disabled.
    // eslint-disable-next-line @typescript-eslint/max-params
    api.setState = ((updater, shouldReplace, nameOrAction: Action, ...rest: unknown[]) => {
      // @ts-expect-error nameOrAction should be passed down along with rest parameters
      set(updater, shouldReplace, nameOrAction, ...rest);

      if (typeof nameOrAction === 'object') {
        const updatedState = onUpdate(nameOrAction, get());

        if (updatedState) {
          // This middleware will perform a second state changes if the onUpdate function returns a new (partial) state.
          // This is due to the nature of Zustand, where the state is updated in a single transaction, either via a function
          // that should return a new (partial) state or by setting a new (partial) state.
          // If the Immer middleware is used the updater function could be used in a mutative way, in this case the state
          // is wrapped in a proxy and sealed in an Object.preventExtensions which will throw an error if the object state is mutated.
          // This is why updating the state via updater(get()) without a call to set() will throw an error.
          // The code that has been tested is as follows:
          // if (typeof updater === 'function') {
          //   const state = get();
          //   updater(state);
          // }
          // An alternative solution would be to clone the state via JSON.parse(JSON.stringify(get())) but this would potentially be
          // slow for large states.
          // @ts-expect-error nameOrAction should be passed down
          set(updatedState, undefined, actionName ?? 'stateChanged');
        }
      }
    }) as NamedSet<TState>;

    return initializer(api.setState, get, api);
  };

/**
 * Zustand middleware used to intercept state changes based on actions.
 *
 * Inspiration taken from:
 * https://zustand.docs.pmnd.rs/guides/typescript#authoring-middlewares-and-advanced-usage
 *
 * Implementation uses higher-kinded mutator:
 * https://github.com/pmndrs/zustand/issues/710
 *
 * @param initializer
 * @returns
 */
export const stateChanged = stateChangedImpl as unknown as StateChanged;
